import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import * as api from "../api";
import { useAuth } from "../context/AuthContext";

const couleurs = ["#0d9488", "#6366f1", "#f59e0b", "#ec4899", "#8b5cf6", "#dc2626", "#0891b2", "#059669"];

function ExposantDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("annonces");
  const [exposant, setExposant] = useState(null);
  const [exposantAnnonces, setExposantAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  // ✅ Système de notation
  const [ratings, setRatings] = useState({ moyenne: 0, total: 0, avis: [], myNote: null });
  const [ratingNote, setRatingNote] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingHover, setRatingHover] = useState(0);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [ratingMsg, setRatingMsg] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [expRes, annRes] = await Promise.all([
          api.getExposants(),
          api.getAnnonces({ limit: 100 })
        ]);
        const realUser = expRes.data.find(u => u.id === parseInt(id));
        if (realUser) {
          const index = expRes.data.findIndex(u => u.id === parseInt(id));
          setExposant({
            id: realUser.id,
            entreprise: realUser.entreprise || `${realUser.prenom} ${realUser.nom}`,
            nom: `${realUser.prenom} ${realUser.nom}`,
            localisation: "Algérie",
            categorie: "Maritime",
            description: "Exposant professionnel sur MarinaDZ.",
            descriptionLongue: "Exposant professionnel sur MarinaDZ.",
            telephone: realUser.telephone || null,
            email: realUser.email,
            siteWeb: realUser.site_web || null,
            membre: realUser.created_at ? new Date(realUser.created_at).toLocaleDateString("fr-FR") : "—",
            initiales: `${realUser.prenom?.[0] || ""}${realUser.nom?.[0] || ""}`.toUpperCase(),
            couleur: couleurs[index % couleurs.length],
            anneeCreation: realUser.created_at ? new Date(realUser.created_at).getFullYear() : "—",
            plan: realUser.plan || "gratuit",
            badge: realUser.badge || "none",
            logo: realUser.avatar || null
          });
          const siennesActives = (annRes.data.annonces || []).filter(a => a.exposant_id === parseInt(id));
          setExposantAnnonces(siennesActives);
          try {
            const ratingRes = await api.getRatings(id);
            setRatings(ratingRes.data);
            setRatingNote(ratingRes.data.myNote || 0);
          } catch (e) {
            console.error("Erreur chargement ratings:", e?.response?.data?.message || e.message);
            // La table ratings n'existe peut-être pas encore — afficher score vide
            setRatings({ moyenne: 0, total: 0, avis: [], myNote: null });
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <Navbar />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: "40px", height: "40px", border: "3px solid #e5e7eb", borderTop: "3px solid #0d9488", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
        <Footer />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!exposant) {
    return (
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <Navbar />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", padding: "60px" }}>
          <h1 style={{ fontSize: "24px", color: "#1f2937", marginBottom: "20px" }}>Exposant non trouvé</h1>
          <Link to="/exposants" style={{ backgroundColor: "#0d9488", color: "white", padding: "12px 24px", borderRadius: "8px", textDecoration: "none", fontWeight: "600" }}>
            Retour aux exposants
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const handleSubmitRating = async () => {
    if (!ratingNote) return;
    if (!user) { setRatingMsg("❌ Connectez-vous pour laisser un avis"); return; }
    setRatingLoading(true);
    try {
      await api.submitRating(id, { note: ratingNote, commentaire: ratingComment });
      const res = await api.getRatings(id);
      setRatings(res.data);
      setRatingNote(res.data.myNote || ratingNote);
      setRatingComment("");
      setRatingMsg("✅ Merci pour votre avis !");
      setTimeout(() => setRatingMsg(null), 3000);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Erreur serveur";
      setRatingMsg("❌ " + msg);
      setTimeout(() => setRatingMsg(null), 4000);
    } finally { setRatingLoading(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Navbar />

      {/* Hero Banner — caché sur le mini-site */}
      {activeTab !== "minisite" && (
      <div className="exposant-detail-hero" style={{
        background: `linear-gradient(135deg, ${exposant.couleur}15 0%, ${exposant.couleur}05 100%)`,
        borderBottom: `3px solid ${exposant.couleur}30`,
      }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* Breadcrumb */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "24px", fontSize: "14px", flexWrap: "wrap" }}>
            <Link to="/" style={{ color: "#6b7280", textDecoration: "none" }}>Accueil</Link>
            <span style={{ color: "#d1d5db" }}>/</span>
            <Link to="/exposants" style={{ color: "#6b7280", textDecoration: "none" }}>Exposants</Link>
            <span style={{ color: "#d1d5db" }}>/</span>
            <span style={{ color: exposant.couleur }}>{exposant.entreprise}</span>
          </div>

          <div className="exposant-detail-hero-inner">
            {/* Avatar */}
            <div style={{ width: "100px", height: "100px", borderRadius: "20px", flexShrink: 0, backgroundColor: exposant.couleur, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "32px", fontWeight: "700", color: "white", boxShadow: `0 8px 30px ${exposant.couleur}40` }}>
              {exposant.logo ? <img src={exposant.logo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "20px" }} /> : exposant.initiales}
            </div>

            {/* Infos */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ fontSize: "28px", fontWeight: "700", color: "#1f2937", margin: "0 0 8px" }}>{exposant.entreprise}</h1>
              <p style={{ fontSize: "15px", color: "#6b7280", marginBottom: "16px" }}>
                par <strong style={{ color: "#374151" }}>{exposant.nom}</strong> · Membre depuis {exposant.membre}
              </p>
              {/* Badge plan */}
              {exposant.plan && exposant.plan !== "gratuit" && (
                <div style={{ marginBottom: "12px" }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    padding: "4px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "700",
                    backgroundColor:
                      exposant.plan === "premium_enterprise" ? "#1e1b4b" :
                      exposant.plan === "premium_business"   ? "#1e3a5f" : "#0f4c41",
                    color: "white"
                  }}>
                    {exposant.plan === "premium_enterprise" ? "👑 Enterprise" :
                     exposant.plan === "premium_business"   ? "⭐ Business"   : "✅ Starter"}
                  </span>
                </div>
              )}
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "14px", color: "#6b7280" }}>📍 {exposant.localisation}</span>
                <span style={{ fontSize: "14px", color: "#6b7280" }}> {exposantAnnonces.length} annonce{exposantAnnonces.length > 1 ? "s" : ""}</span>
                {ratings.moyenne > 0 && (
                  <span style={{ fontSize: "14px", color: "#f59e0b", display: "flex", alignItems: "center", gap: "4px" }}>
                    ⭐ {ratings.moyenne}/5
                    <span style={{ color: "#9ca3af", fontSize: "13px" }}>({ratings.total} avis)</span>
                  </span>
                )}
                <span style={{ fontSize: "14px", color: "#6b7280" }}>🏢 Fondé en {exposant.anneeCreation}</span>
                {exposant.siteWeb && (
                  <a href={exposant.siteWeb.startsWith("http") ? exposant.siteWeb : `https://${exposant.siteWeb}`} target="_blank" rel="noreferrer" style={{ fontSize: "14px", color: "#0d9488", textDecoration: "none", display: "flex", alignItems: "center", gap: "4px" }}>
                    🌐 {exposant.siteWeb}
                  </a>
                )}
              </div>
            </div>

            {/* Téléphone — numéro complet */}
            <div className="exposant-detail-actions">
              {exposant.telephone && (
                <a href={`tel:${exposant.telephone}`} style={{ display: "flex", alignItems: "center", gap: "8px", backgroundColor: exposant.couleur, color: "white", padding: "12px 22px", borderRadius: "10px", textDecoration: "none", fontSize: "14px", fontWeight: "600", justifyContent: "center" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013 12.79a19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                  {exposant.telephone}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Main Content */}
      <div className="exposant-detail-main" style={{ flex: 1, backgroundColor: "#f9fafb" }}>
        <div className={activeTab === "minisite" ? "" : "exposant-detail-inner"} style={activeTab === "minisite" ? { maxWidth: "1100px", margin: "0 auto", padding: "16px 20px" } : {}}>

          <div style={{ minWidth: 0 }}>
            {/* Tabs — cachés sur mini-site */}
            {activeTab !== "minisite" && (
            <div style={{ display: "flex", backgroundColor: "white", borderRadius: "12px", padding: "5px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: "25px", flexWrap:"wrap", gap:"2px" }}>
              {[
                { id: "annonces", label: `Annonces (${exposantAnnonces.length})` },
                { id: "apropos", label: "À propos" },
                { id: "contact", label: "Contact" },
                { id: "score", label: `⭐ Score (${ratings.total})` },
                ...(exposant.plan === "premium_enterprise" ? [{ id: "minisite", label: "🌐 Mini-site" }] : [])
              ].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ flex: 1, minWidth:"80px", padding: "10px", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: activeTab === tab.id ? "700" : "500", backgroundColor: activeTab === tab.id ? exposant.couleur : "transparent", color: activeTab === tab.id ? "white" : "#6b7280", transition: "all 0.2s" }}>
                  {tab.label}
                </button>
              ))}
            </div>
            )}

            {/* Tabs visibles sur mini-site — cachés, bouton retour mis en bas */}
            {activeTab === "minisite" && (<span />)}

            {/* Annonces Tab */}
            {activeTab === "annonces" && (
              <div>
                {exposantAnnonces.length > 0 ? (
                  <div className="grid-responsive" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px" }}>
                    {exposantAnnonces.map(annonce => (
                      <Link key={annonce.id} to={`/annonce/${annonce.id}`} style={{ textDecoration: "none", backgroundColor: "white", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid #f3f4f6" }}
                        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.12)"; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)"; }}>
                        <div style={{ position: "relative" }}>
                          <img src={annonce.image || "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=400"} alt={annonce.nom}
                            style={{ width: "100%", height: "160px", objectFit: "cover" }}
                            onError={e => { e.target.src = "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=400"; }} />
                          {annonce.isNew && <span style={{ position: "absolute", top: "10px", left: "10px", backgroundColor: "#0d9488", color: "white", padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "600" }}>Nouveau</span>}
                        </div>
                        <div style={{ padding: "16px" }}>
                          <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", marginBottom: "6px" }}>{annonce.nom}</h3>
                          <p style={{ fontSize: "15px", fontWeight: "700", color: "#0d9488" }}>{annonce.prix}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "60px", backgroundColor: "white", borderRadius: "12px" }}>
                    <p style={{ color: "#9ca3af", fontSize: "15px" }}>Aucune annonce publiée pour l'instant.</p>
                  </div>
                )}
              </div>
            )}

            {/* À propos Tab */}
            {activeTab === "apropos" && (
              <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "30px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#1f2937", marginBottom: "16px" }}>À propos de {exposant.entreprise}</h2>
                <p style={{ fontSize: "15px", color: "#4b5563", lineHeight: "1.8" }}>{exposant.descriptionLongue}</p>
              </div>
            )}

            {/* Contact Tab — ✅ Sans bouton email, avec site web */}
            {activeTab === "contact" && (
              <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "30px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                <h2 style={{ fontSize: "18px", fontWeight: "700", color: "#1f2937", marginBottom: "20px" }}>Coordonnées</h2>

                {exposant.telephone && (
                  <>
                    <a href={`tel:${exposant.telephone}`} style={{ display: "flex", alignItems: "center", gap: "16px", padding: "18px 0", borderBottom: "1px solid #f3f4f6", textDecoration: "none" }}>
                      <div style={{ width: "42px", height: "42px", borderRadius: "50%", backgroundColor: "#f0fdfa", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013 12.79a19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                      </div>
                      <div>
                        <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "2px" }}>Téléphone</div>
                        <div style={{ fontSize: "15px", color: "#1f2937", fontWeight: "500" }}>{exposant.telephone}</div>
                      </div>
                    </a>
                    
                  </>
                )}

                {/* ✅ Site web affiché dans contact */}
                {exposant.siteWeb && (
                  <a href={exposant.siteWeb.startsWith("http") ? exposant.siteWeb : `https://${exposant.siteWeb}`} target="_blank" rel="noreferrer"
                    style={{ display: "flex", alignItems: "center", gap: "16px", padding: "18px 0", borderBottom: "1px solid #f3f4f6", textDecoration: "none" }}>
                    <div style={{ width: "42px", height: "42px", borderRadius: "50%", backgroundColor: "#f0fdfa", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "2px" }}>Site web</div>
                      <div style={{ fontSize: "15px", color: "#0d9488", fontWeight: "500" }}>{exposant.siteWeb}</div>
                    </div>
                  </a>
                )}

                <div style={{ marginTop: "24px", padding: "16px", backgroundColor: "#f0fdfa", borderRadius: "10px", border: "1px solid #0d9488" }}>
                  <p style={{ fontSize: "13px", color: "#065f46", margin: 0 }}>
                    💬 Pour contacter cet exposant, utilisez le formulaire de demande de devis ou appelez directement.
                  </p>
                </div>
              </div>
            )}

            {/* ✅ Tab Score / Notation */}
            {activeTab === "score" && (
              <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "30px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                <h2 style={{ fontSize: "18px", fontWeight: "700", color: "#1f2937", marginBottom: "20px" }}>⭐ Score & Avis</h2>

                {/* Résumé */}
                <div style={{ display: "flex", alignItems: "center", gap: "24px", marginBottom: "30px", padding: "20px", backgroundColor: "#fffbeb", borderRadius: "12px", border: "1px solid #fcd34d" }}>
                  <div style={{ textAlign: "center", flexShrink: 0 }}>
                    <div style={{ fontSize: "52px", fontWeight: "800", color: "#f59e0b", lineHeight: 1 }}>{ratings.moyenne > 0 ? ratings.moyenne : "—"}</div>
                    <div style={{ fontSize: "12px", color: "#92400e", marginTop: "4px" }}>{ratings.total} avis</div>
                  </div>
                  <div>
                    <div style={{ display: "flex", gap: "4px", marginBottom: "8px" }}>
                      {[1,2,3,4,5].map(s => (
                        <svg key={s} width="28" height="28" viewBox="0 0 24 24" fill={s <= Math.round(ratings.moyenne) ? "#f59e0b" : "#e5e7eb"} stroke="none">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                        </svg>
                      ))}
                    </div>
                    <div style={{ fontSize: "13px", color: "#6b7280" }}>Note moyenne basée sur {ratings.total} évaluation{ratings.total > 1 ? "s" : ""}</div>
                  </div>
                </div>

                {/* Formulaire de notation */}
                {user && user.id !== exposant.id ? (
                  <div style={{ marginBottom: "30px", padding: "20px", border: "2px solid #e5e7eb", borderRadius: "12px" }}>
                    <h3 style={{ fontSize: "15px", fontWeight: "700", color: "#1f2937", marginBottom: "14px" }}>
                      {ratings.myNote ? "✏️ Modifier mon avis" : "⭐ Donner mon avis"}
                    </h3>
                    <div style={{ display: "flex", gap: "6px", marginBottom: "14px" }}>
                      {[1,2,3,4,5].map(s => (
                        <svg key={s} width="38" height="38" viewBox="0 0 24 24"
                          fill={s <= (ratingHover || ratingNote) ? "#f59e0b" : "#d1d5db"} stroke="none"
                          style={{ cursor: "pointer", transition: "transform 0.1s", transform: ratingHover === s ? "scale(1.2)" : "scale(1)" }}
                          onMouseEnter={() => setRatingHover(s)}
                          onMouseLeave={() => setRatingHover(0)}
                          onClick={() => setRatingNote(s)}>
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                        </svg>
                      ))}
                      {ratingNote > 0 && (
                        <span style={{ marginLeft: "10px", fontSize: "14px", color: "#f59e0b", fontWeight: "700", alignSelf: "center" }}>
                          {["", "Très mauvais", "Mauvais", "Correct", "Bien", "Excellent"][ratingNote]}
                        </span>
                      )}
                    </div>
                    <textarea value={ratingComment} onChange={e => setRatingComment(e.target.value)}
                      placeholder="Partagez votre expérience (optionnel)..." rows={3}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: "14px", outline: "none", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }} />
                    {ratingMsg && (
                      <div style={{ marginTop: "8px", fontSize: "13px", fontWeight: "600", color: ratingMsg.startsWith("✅") ? "#0d9488" : "#dc2626" }}>
                        {ratingMsg}
                      </div>
                    )}
                    <button onClick={handleSubmitRating} disabled={!ratingNote || ratingLoading}
                      style={{ marginTop: "12px", padding: "10px 28px", backgroundColor: ratingNote && !ratingLoading ? "#0d9488" : "#9ca3af", color: "white", border: "none", borderRadius: "8px", fontWeight: "600", cursor: ratingNote ? "pointer" : "not-allowed", fontSize: "14px" }}>
                      {ratingLoading ? "Envoi..." : ratings.myNote ? "Mettre à jour" : "Publier mon avis"}
                    </button>
                  </div>
                ) : !user ? (
                  <div style={{ marginBottom: "20px", padding: "16px", backgroundColor: "#f9fafb", borderRadius: "10px", textAlign: "center" }}>
                    <p style={{ color: "#6b7280", fontSize: "14px", margin: 0 }}>
                      <a href="/connexion" style={{ color: "#0d9488", fontWeight: "600" }}>Connectez-vous</a> pour laisser un avis sur cet exposant.
                    </p>
                  </div>
                ) : null}

                {/* Liste des avis */}
                <h3 style={{ fontSize: "15px", fontWeight: "700", color: "#1f2937", marginBottom: "16px" }}>
                  Tous les avis ({ratings.total})
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  {ratings.avis.length > 0 ? ratings.avis.map((a, i) => (
                    <div key={i} style={{ padding: "16px", backgroundColor: "#f9fafb", borderRadius: "10px", border: "1px solid #f3f4f6" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div style={{ width: "36px", height: "36px", borderRadius: "50%", backgroundColor: "#0d9488", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "700", color: "white", flexShrink: 0 }}>
                            {a.prenom?.[0]}{a.nom?.[0]}
                          </div>
                          <div>
                            <div style={{ fontWeight: "600", fontSize: "14px", color: "#1f2937" }}>{a.prenom} {a.nom}</div>
                            <div style={{ fontSize: "12px", color: "#9ca3af" }}>{new Date(a.created_at).toLocaleDateString("fr-FR")}</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "2px" }}>
                          {[1,2,3,4,5].map(s => (
                            <svg key={s} width="15" height="15" viewBox="0 0 24 24" fill={s <= a.note ? "#f59e0b" : "#e5e7eb"} stroke="none">
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                            </svg>
                          ))}
                        </div>
                      </div>
                      {a.commentaire && <p style={{ fontSize: "13px", color: "#4b5563", margin: 0, lineHeight: "1.6" }}>{a.commentaire}</p>}
                    </div>
                  )) : (
                    <div style={{ textAlign: "center", padding: "40px", color: "#9ca3af" }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" style={{ margin: "0 auto 12px", display: "block" }}>
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                      <p style={{ fontSize: "15px" }}>Aucun avis pour le moment. Soyez le premier !</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mini-site Enterprise */}
            {activeTab === "minisite" && exposant.plan === "premium_enterprise" && (
              <div>
                {/* Header compact */}
                <div style={{ backgroundColor: "white", borderRadius: "14px", padding: "18px 22px", marginBottom: "14px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
                  <div style={{ width: "100px", height: "100px", borderRadius: "12px", flexShrink: 0, backgroundColor: exposant.couleur, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", fontWeight: "800", color: "white", overflow: "hidden" }}>
                    {exposant.logo ? <img src={exposant.logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : exposant.initiales}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
                      <h2 style={{ fontSize: "17px", fontWeight: "700", color: "#1f2937", margin: 0 }}>{exposant.entreprise}</h2>
                      <span style={{ padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "700", backgroundColor: "#1e1b4b", color: "white" }}>👑 Enterprise</span>
                    </div>
                    <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
                      <span style={{ fontSize: "12px", color: "#6b7280" }}>📦 {exposantAnnonces.length} produits</span>
                      {ratings.moyenne > 0 && <span style={{ fontSize: "12px", color: "#f59e0b" }}>⭐ {ratings.moyenne}/5 ({ratings.total} avis)</span>}
                      {exposant.siteWeb && <a href={exposant.siteWeb.startsWith("http") ? exposant.siteWeb : `https://${exposant.siteWeb}`} target="_blank" rel="noreferrer" style={{ fontSize: "12px", color: exposant.couleur, textDecoration: "none" }}>🌐 {exposant.siteWeb}</a>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", flexShrink: 0, flexWrap: "wrap" }}>
                    {exposant.telephone && (
                      <a href={`tel:${exposant.telephone}`} style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: exposant.couleur, color: "white", padding: "9px 16px", borderRadius: "8px", textDecoration: "none", fontSize: "13px", fontWeight: "600" }}>
                        📞 {exposant.telephone}
                      </a>
                    )}
                    <Link to={`/rfq?annonceNom=${encodeURIComponent(exposant.entreprise)}`}
                      style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "white", color: exposant.couleur, border: `1.5px solid ${exposant.couleur}`, padding: "9px 16px", borderRadius: "8px", textDecoration: "none", fontSize: "13px", fontWeight: "600" }}>
                      📋 Demander un devis
                    </Link>
                  </div>
                </div>

                {/* Layout produits + sidebar */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: "14px", alignItems: "start" }}>

                  {/* Produits */}
                  <div>
                    <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "12px" }}>
                      <strong style={{ color: "#1f2937" }}>{exposantAnnonces.length}</strong> No{exposantAnnonces.length > 1 ? "s" : ""} produit{exposantAnnonces.length > 1 ? "s" : ""}
                    </p>
                    {exposantAnnonces.length > 0 ? (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "12px" }}>
                        {exposantAnnonces.map(a => (
                          <Link key={a.id} to={`/annonce/${a.id}`} style={{ textDecoration: "none", backgroundColor: "white", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", border: "1px solid #f1f5f9", display: "block", transition: "all 0.2s" }}
                            onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                            onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                            <div style={{ position: "relative" }}>
                              <img src={a.image || "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=300"} alt={a.nom}
                                style={{ width: "100%", height: "120px", objectFit: "cover", display: "block" }}
                                onError={e => { e.target.src = "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=300"; }} />
                              {a.is_new && <span style={{ position: "absolute", top: "8px", left: "8px", backgroundColor: "#0d9488", color: "white", fontSize: "10px", fontWeight: "700", padding: "2px 7px", borderRadius: "6px" }}>Neuf</span>}
                            </div>
                            <div style={{ padding: "10px 12px" }}>
                              <p style={{ fontSize: "12px", fontWeight: "600", color: "#1f2937", marginBottom: "6px", lineHeight: "1.4", minHeight: "32px" }}>{a.nom}</p>
                              <p style={{ fontSize: "13px", fontWeight: "700", color: exposant.couleur, margin: 0 }}>{a.prix || "Sur demande"}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "40px", textAlign: "center" }}>
                        <p style={{ color: "#9ca3af", fontSize: "14px" }}>Aucun produit publié pour l'instant.</p>
                      </div>
                    )}
                  </div>

                  {/* Sidebar */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                      <p style={{ fontSize: "11px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "12px" }}>Contact</p>
                      {exposant.telephone && (
                        <a href={`tel:${exposant.telephone}`} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", backgroundColor: `${exposant.couleur}10`, borderRadius: "8px", textDecoration: "none", marginBottom: "8px" }}>
                          <span style={{ fontSize: "16px" }}>📞</span>
                          <span style={{ fontSize: "13px", fontWeight: "600", color: exposant.couleur }}>{exposant.telephone}</span>
                        </a>
                      )}
                      {exposant.siteWeb && (
                        <a href={exposant.siteWeb.startsWith("http") ? exposant.siteWeb : `https://${exposant.siteWeb}`} target="_blank" rel="noreferrer"
                          style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", backgroundColor: "#f9fafb", borderRadius: "8px", textDecoration: "none", marginBottom: "8px" }}>
                          <span style={{ fontSize: "16px" }}>🌐</span>
                          <span style={{ fontSize: "12px", color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{exposant.siteWeb}</span>
                        </a>
                      )}
                      <Link to={`/rfq?annonceNom=${encodeURIComponent(exposant.entreprise)}`}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "11px", backgroundColor: exposant.couleur, color: "white", borderRadius: "8px", textDecoration: "none", fontSize: "13px", fontWeight: "700" }}>
                        📋 Demander un devis
                      </Link>
                    </div>

                    <div style={{ backgroundColor: "#1e1b4b", borderRadius: "12px", padding: "16px" }}>
                      <p style={{ fontSize: "11px", fontWeight: "700", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "10px" }}>👑 Enterprise</p>
                      {["Profil vérifié", "Annonces prioritaires", "Statistiques avancées", "Messagerie illimitée", "Mini-site dédié", "Support prioritaire"].map(f => (
                        <div key={f} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                          <span style={{ color: "#0d9488", fontSize: "12px", fontWeight: "700" }}>✓</span>
                          <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.85)" }}>{f}</span>
                        </div>
                      ))}
                    </div>

                    <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                      <p style={{ fontSize: "11px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "12px" }}>Informations</p>
                      {[
                        { label: "Membre depuis", value: exposant.membre },
                        { label: "Annonces", value: exposantAnnonces.length },
                        { label: "Score", value: ratings.moyenne > 0 ? `⭐ ${ratings.moyenne}/5` : "—" },
                      ].map(f => (
                        <div key={f.label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f9fafb" }}>
                          <span style={{ fontSize: "12px", color: "#9ca3af" }}>{f.label}</span>
                          <span style={{ fontSize: "12px", fontWeight: "600", color: "#1f2937" }}>{f.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bouton retour en bas */}
                <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
                  <button onClick={() => setActiveTab("annonces")} style={{ padding: "10px 24px", backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px", cursor: "pointer", fontSize: "13px", color: "#6b7280", fontWeight: "500" }}>
                    ← Retour au profil
                  </button>
                </div>
              </div>
            )}

          </div>

          {/* Right Sidebar — cachée sur mini-site */}
          {activeTab !== "minisite" && (
          <div className="exposant-detail-sidebar">
            <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: "20px" }}>
              <h3 style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>Catégorie</h3>
              <span style={{ display: "inline-block", backgroundColor: exposant.couleur + "15", color: exposant.couleur, padding: "6px 16px", borderRadius: "20px", fontSize: "13px", fontWeight: "700", border: `1px solid ${exposant.couleur}30` }}>
                {exposant.categorie}
              </span>
            </div>

            {/* Avantages du plan */}
            {exposant.plan && exposant.plan !== "gratuit" && (
              <div style={{
                borderRadius: "12px", padding: "20px", marginBottom: "20px",
                background:
                  exposant.plan === "premium_enterprise" ? "linear-gradient(135deg, #1e1b4b, #312e81)" :
                  exposant.plan === "premium_business"   ? "linear-gradient(135deg, #1e3a5f, #1e40af)" :
                  "linear-gradient(135deg, #0f4c41, #0d9488)"
              }}>
                <h3 style={{ fontSize: "13px", fontWeight: "700", color: "white", marginBottom: "10px" }}>
                  {exposant.plan === "premium_enterprise" ? "👑 Exposant Enterprise" :
                   exposant.plan === "premium_business"   ? "⭐ Exposant Business"   : "✅ Exposant Starter"}
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {[
                    "✔ Profil vérifié",
                    "✔ Annonces prioritaires",
                    exposant.plan === "premium_business" || exposant.plan === "premium_enterprise" ? "✔ Statistiques avancées" : null,
                    exposant.plan === "premium_business" || exposant.plan === "premium_enterprise" ? "✔ Messagerie illimitée" : null,
                    exposant.plan === "premium_enterprise" ? "✔ Mini-site dédié" : null,
                    exposant.plan === "premium_enterprise" ? "✔ Support prioritaire" : null,
                  ].filter(Boolean).map((f, i) => (
                    <span key={i} style={{ fontSize: "12px", color: "rgba(255,255,255,0.9)" }}>{f}</span>
                  ))}
                </div>
              </div>
            )}

            {/* ✅ Infos rapides */}
            <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: "20px" }}>
              <h3 style={{ fontSize: "14px", fontWeight: "700", color: "#1f2937", marginBottom: "14px" }}>Informations</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "13px", color: "#6b7280" }}>Annonces</span>
                  <span style={{ fontSize: "13px", fontWeight: "700", color: "#1f2937" }}>{exposantAnnonces.length}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "13px", color: "#6b7280" }}>Membre depuis</span>
                  <span style={{ fontSize: "13px", fontWeight: "700", color: "#1f2937" }}>{exposant.membre}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "13px", color: "#6b7280" }}>Wilaya</span>
                  <span style={{ fontSize: "13px", fontWeight: "700", color: "#1f2937" }}>{exposant.localisation}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "13px", color: "#6b7280" }}>Score</span>
                  <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="#f59e0b" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                    <span style={{ fontSize: "13px", fontWeight: "700", color: "#1f2937" }}>{ratings.moyenne > 0 ? ratings.moyenne : "—"}</span>
                    <span style={{ fontSize: "11px", color: "#9ca3af" }}>({ratings.total})</span>
                  </span>
                </div>
              </div>
            </div>

            <div style={{ backgroundColor: exposant.couleur, borderRadius: "12px", padding: "24px", textAlign: "center" }}>
              <h3 style={{ fontSize: "15px", fontWeight: "700", color: "white", marginBottom: "8px" }}>Demander un devis</h3>
              <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.85)", marginBottom: "16px", lineHeight: "1.5" }}>
                Obtenez une offre personnalisée de {exposant.entreprise}
              </p>
              <Link to={`/rfq?annonceNom=${encodeURIComponent(exposant.entreprise)}`} style={{ display: "block", backgroundColor: "white", color: exposant.couleur, padding: "11px", borderRadius: "8px", textDecoration: "none", fontSize: "13px", fontWeight: "700" }}>
                Faire une demande RFQ
              </Link>
            </div>
          </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Footer />
    </div>
  );
}

export default ExposantDetail;